import { useCallback, useMemo } from "react"
import { useAuthHeader, useAuthInfo } from "../context/AuthContext"
import { getEnv } from "../utils/envManager"
import { safeFetchJSON } from "../utils/safe-fetch"
import {
  useResourceSharePanel as useSharedResourceSharePanel,
  type IResourceSharePanel,
  type IShareRequestInit,
} from "@common/hooks/use-resource-share-panel"
import type { OrgUser } from "@common/components/share/types"

export function useResourceSharePanel(basePath: string, resourceId: string): IResourceSharePanel {
  const authHeader = useAuthHeader()
  const { user: authUser } = useAuthInfo()

  const userId = authUser?.userId
  const email = authUser?.email
  const firstName = authUser?.firstName
  const lastName = authUser?.lastName
  const currentUser: OrgUser | null = useMemo(
    () => userId ? { userId, email, firstName: firstName || undefined, lastName: lastName || undefined } : null,
    [userId, email, firstName, lastName],
  )

  const fetchJson = useCallback(async (path: string, init?: IShareRequestInit) => {
    if (!authHeader) throw new Error("Sign in to manage sharing")
    return safeFetchJSON(`${getEnv("WEBAPP_API_BASE_URL")}${path}`, {
      method: init?.method ?? "GET",
      headers: { Authorization: authHeader, "Content-Type": "application/json" },
      body: init?.body,
    })
  }, [authHeader])

  return useSharedResourceSharePanel({ resourceId, basePath, currentUser, fetchJson })
}
